import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../components/api/ApiClient';

function ForgetPassword() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [otp, setOtp] = useState(['', '', '', '', '']);
    const [newPassword, setNewPassword] = useState('');
    const [rePassword, setRePassword] = useState('');
    const [timer, setTimer] = useState(120);
    const [toast, setToast] = useState({ show: false, message: '', type: 'error' });
    const [showPassword, setShowPassword] = useState(false);
    const [showRePassword, setShowRePassword] = useState(false);
    const otpRefs = useRef([]);

    useEffect(() => {
        let interval = null;
        if (step === 2 && timer > 0) {
            interval = setInterval(() => setTimer((t) => t - 1), 1000);
        } else {
            clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [step, timer]);

    useEffect(() => {
        if (toast.show) {
            const timeout = setTimeout(() => setToast({ ...toast, show: false }), 5000);
            return () => clearTimeout(timeout);
        }
    }, [toast.show]);

    const showToast = (message, type = 'error') => {
        setToast({ show: true, message, type });
    };

    const sendOtpRequest = async () => {
        if (!phoneNumber || phoneNumber.length < 10) {
            showToast('لطفا شماره موبایل معتبر وارد کنید');
            return false;
        }
        setLoading(true);
        try {
            await apiClient(`/account/api/forget-password?phonenumber=${phoneNumber}&state=forget&register=true`, { method: 'GET' });
            setTimer(120);
            setOtp(['', '', '', '', '']);
            return true;
        } catch (error) {
            showToast(error.message || 'خطا در ارسال کد تایید');
            return false;
        } finally {
            setLoading(false);
        }
    };

    const handleSendOtp = async (e) => {
        e.preventDefault();
        const success = await sendOtpRequest();
        if (success) {
            setStep(2);
        }
    };

    const handleResendOtp = async () => {
        const success = await sendOtpRequest();
        if (success) {
            showToast('کد مجددا ارسال شد', 'success');
        }
    };

    const handleOtpChange = (index, value) => {
        if (value.length > 1) {
            const digits = value.replace(/\D/g, '').slice(0, 5).split('');
            const newOtp = [...otp];
            digits.forEach((digit, i) => {
                if (i < 5) newOtp[i] = digit;
            });
            setOtp(newOtp);
            const lastFilledIndex = Math.min(digits.length - 1, 4);
            if (otpRefs.current[lastFilledIndex]) {
                otpRefs.current[lastFilledIndex].focus();
            }
            return;
        }

        if (!/^\d*$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value && index < 4 && otpRefs.current[index + 1]) {
            otpRefs.current[index + 1].focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpRefs.current[index - 1].focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 5);
        if (pastedData) {
            const digits = pastedData.split('');
            const newOtp = [...otp];
            digits.forEach((digit, i) => {
                if (i < 5) newOtp[i] = digit;
            });
            setOtp(newOtp);
            const lastIndex = Math.min(digits.length - 1, 4);
            if (otpRefs.current[lastIndex]) {
                otpRefs.current[lastIndex].focus();
            }
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        const otpCode = otp.join('');
        if (otpCode.length !== 5) {
            showToast('لطفا کد ۵ رقمی را کامل وارد کنید');
            return;
        }
        setLoading(true);
        try {
            const response = await apiClient('/account/api/OtpHandler/', {
                method: 'POST',
                data: { otp: otpCode, phoneNumber: phoneNumber }
            });
            if (response.message === 'Phone and OTP code are required.') {
                showToast('شماره تلفن و کد OTP الزامی هستند');
            } else if (response.message === 'Invalid') {
                showToast('کد ارسالی نامعتبر است');
            } else if (response.message === 'success' || response.status === 'success') {
                showToast('کد وارد شده صحیح است', 'success');
                setTimeout(() => setStep(3), 1000);
            } else {
                showToast(response.message || 'خطا در تایید کد');
            }
        } catch (error) {
            if (error.message.includes('Phone and OTP code are required')) {
                showToast('شماره تلفن و کد OTP الزامی هستند');
            } else if (error.message.includes('Invalid')) {
                showToast('کد ارسالی نامعتبر است');
            } else {
                showToast(error.message || 'خطا در تایید کد');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (newPassword.length < 8) {
            showToast('رمز عبور باید حداقل ۸ کاراکتر باشد');
            return;
        }
        if (newPassword !== rePassword) {
            showToast('رمز عبور و تکرار آن مطابقت ندارند');
            return;
        }
        setLoading(true);
        try {
            const response = await apiClient('/account/api/forget-password/', {
                method: 'POST',
                data: {
                    phoneNumber: phoneNumber,
                    newpassword: newPassword,
                    repassword: rePassword
                }
            });
            if (response.message === 'success' || response.status === 'success') {
                showToast('رمز عبور با موفقیت تغییر یافت', 'success');
                setTimeout(() => navigate('/login/'), 1500);
            } else {
                showToast(response.message || 'خطا در تغییر رمز عبور');
            }
        } catch (error) {
            showToast(error.message || 'خطا در تغییر رمز عبور');
        } finally {
            setLoading(false);
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    const renderStep1 = () => (
        <form onSubmit={handleSendOtp} className="w-100 mt-4 pt-2">
            <h2 className="fs-20 fw-bolder mb-4 text-center">خوش آمدید</h2>
            <h4 className="fs-13 fw-bold mb-4 text-center text-muted">
                برای بازیابی رمز عبور لطفا شماره همراهی که در شبکه آرسین ثبت کرده اید را وارد کنید
            </h4>
            <div className="mb-4">
                <label className="form-label">شماره موبایل :</label>
                <input
                    type="tel"
                    className="form-control"
                    placeholder="09xxxxxxxxx"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    required
                />
            </div>
            <button disabled={loading} type="submit" className="btn btn-lg btn-primary w-100 mb-3">
                {loading ? '...' : 'ثبت شماره'}
            </button>
            <div className="text-center">
                <span
                    className="fw-bold text-secondary cursor-pointer"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate('/login/')}
                >
                    بازگشت به صفحه ورود ←
                </span>
            </div>
        </form>
    );

    const renderStep2 = () => (
        <form onSubmit={handleVerifyOtp} className="w-100 mt-4 pt-2">
            <h2 className="fs-20 fw-bolder mb-4 text-center">خوش آمدید</h2>
            <h4 className="fs-13 fw-bold mb-4 text-center text-muted">
                کد ارسال شده به شماره خود را وارد کنید
            </h4>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="text-muted">کد ارسال شده :</span>
                <span className="fs-16 fw-bold">{formatTime(timer)}</span>
            </div>
            <div className="d-flex flex-row-reverse justify-content-center gap-2 mb-4">
                {otp.map((digit, index) => (
                    <input
                        key={index}
                        ref={(el) => (otpRefs.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        className="form-control text-center rounded"
                        style={{ width: '50px', height: '50px', fontSize: '20px' }}
                        maxLength={5}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        onPaste={handleOtpPaste}
                        required
                    />
                ))}
            </div>
            <button disabled={loading} type="submit" className="btn btn-lg btn-primary w-100 mb-3">
                {loading ? '...' : 'ثبت کد'}
            </button>
            <div className="text-center">
                <span
                    className="fw-bold text-secondary cursor-pointer"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setStep(1)}
                >
                    بازگشت ←
                </span>
            </div>
            {timer === 0 && (
                <div className="text-center mt-3">
                    <span
                        className="fw-bold text-primary cursor-pointer"
                        style={{ cursor: 'pointer' }}
                        onClick={handleResendOtp}
                    >
                        ارسال مجدد کد
                    </span>
                </div>
            )}
        </form>
    );

    const renderStep3 = () => (
        <form onSubmit={handleResetPassword} className="w-100 mt-4 pt-2">
            <h2 className="fs-20 fw-bolder mb-4 text-center">به صفحه تغییر رمز خوش آمدید</h2>
            <h4 className="fs-13 fw-bold mb-4 text-center text-muted">
                رمز جدید حساب کاربری خود را وارد کنید
            </h4>
            <div className="mb-3">
                <label className="form-label">شماره همراه:</label>
                <input
                    type="tel"
                    className="form-control"
                    value={phoneNumber}
                    disabled
                    dir="ltr"
                />
            </div>
            <div className="mb-3">
                <label className="form-label">رمز عبور جدید:</label>
                <div className="position-relative">
                    <input
                        type={showPassword ? 'text' : 'password'}
                        className="form-control"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={8}
                    />
                    <i
                        className={`feather-eye${showPassword ? '-off' : ''}`}
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '10px',
                            transform: 'translateY(-50%)',
                            cursor: 'pointer',
                            zIndex: 10,
                            color: '#6c757d'
                        }}
                        onClick={() => setShowPassword(!showPassword)}
                    />
                </div>
            </div>
            <div className="mb-4">
                <label className="form-label">تکرار رمز عبور:</label>
                <div className="position-relative">
                    <input
                        type={showRePassword ? 'text' : 'password'}
                        className="form-control"
                        value={rePassword}
                        onChange={(e) => setRePassword(e.target.value)}
                        required
                        minLength={8}
                    />
                    <i
                        className={`feather-eye${showRePassword ? '-off' : ''}`}
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '10px',
                            transform: 'translateY(-50%)',
                            cursor: 'pointer',
                            zIndex: 10,
                            color: '#6c757d'
                        }}
                        onClick={() => setShowRePassword(!showRePassword)}
                    />
                </div>
            </div>
            <button disabled={loading} type="submit" className="btn btn-lg btn-primary w-100 mb-3">
                {loading ? '...' : 'ثبت رمز جدید'}
            </button>
            <div className="text-center">
                <button
                    type="button"
                    className="btn btn-outline-secondary w-100"
                    onClick={() => navigate('/login/')}
                >
                    بازگشت به صفحه ورود
                </button>
            </div>
        </form>
    );

    return (
        <main className="auth-cover-wrapper position-relative">
            {toast.show && (
                <div
                    aria-live="polite"
                    aria-atomic="true"
                    className="position-fixed p-3"
                    style={{ zIndex: 9999, top: 0, right: 0 }}
                >
                    <div className="toast show" role="alert" aria-live="assertive" aria-atomic="true">
                        <div className="toast-header" style={{ direction: 'rtl' }}>
                            {toast.type === 'success' ? (
                                <span className="text-success me-2">✓</span>
                            ) : (
                                <span className="text-danger me-2">✗</span>
                            )}
                            <strong className="me-auto ms-2">
                                {toast.type === 'success' ? 'موفق' : 'خطا'}
                            </strong>
                        </div>
                        <div className="toast-body">{toast.message}</div>
                    </div>
                </div>
            )}
            <div className="auth-cover-sidebar-inner">
                <div className="position-absolute top-0 start-0 m-4" style={{ zIndex: 10 }}>
                    <button
                        type="button"
                        className="btn btn-light shadow-sm d-flex align-items-center gap-2"
                        onClick={() => {
                            if (step > 1) {
                                setStep(step - 1);
                            } else {
                                navigate('/login/');
                            }
                        }}
                    >
                        <i className="feather-arrow-right"></i>
                        <span>بازگشت</span>
                    </button>
                </div>
                <div className="auth-cover-card-wrapper">
                    <div className="auth-cover-card p-sm-5">
                        <div className="wd-50 mb-3">
                            <img src="/assets/images/logo-abbr.png" alt="" className="img-fluid" />
                        </div>
                        {step === 1 && renderStep1()}
                        {step === 2 && renderStep2()}
                        {step === 3 && renderStep3()}
                    </div>
                </div>
            </div>
            <div className="auth-cover-content-inner">
                <div className="auth-cover-content-wrapper">
                    <div className="auth-img">
                        <img src="/assets/images/auth/auth-cover-reset-bg.svg" alt="" className="img-fluid" />
                    </div>
                </div>
            </div>
        </main>
    );
}

export default ForgetPassword;
